/**
 * Pipeline coordinator that manages and runs multiple enrichers.
 * Enrichers are run in priority order (highest first).
 */

import type { BaseEnricher } from './BaseEnricher.js';
import type { EnricherContext } from './types.js';

export class PreviewEnricher {
  private enrichers: BaseEnricher[] = [];

  /**
   * Register an enricher to the pipeline.
   */
  register(enricher: BaseEnricher): void {
    this.enrichers.push(enricher);
    // Sort by priority descending (higher priority runs first)
    this.enrichers.sort((a, b) => b.priority - a.priority);
  }

  /**
   * Unregister an enricher by name or instance.
   */
  unregister(enricherOrName: string | BaseEnricher): void {
    const name = typeof enricherOrName === 'string' ? enricherOrName : enricherOrName.name;
    this.enrichers = this.enrichers.filter(e => e.name !== name);
  }

  /**
   * Run all registered enrichers on the container.
   */
  async enrich(container: HTMLElement, context: EnricherContext = {}): Promise<void> {
    for (const enricher of this.enrichers) {
      if (enricher.shouldRun(context)) {
        try {
          await enricher.process(container, context);
        } catch (error) {
          console.error(`Enricher ${enricher.name} failed:`, error);
        }
      }
    }
  }

  /**
   * Clean up all enrichers.
   */
  teardown(): void {
    this.enrichers.forEach(e => e.teardown());
  }

  /**
   * Get list of registered enricher names.
   */
  getRegistered(): string[] {
    return this.enrichers.map(e => e.name);
  }
}
